import { ThreeEvent } from '@react-three/fiber';
import { use3DRightClick } from 'hooks/use-3d-right-click';
import { DirectPointDragHandlers } from 'hooks/use-direct-point-drag';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Mesh } from 'three';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder
} from 'utils/3d-opacity';

type DuctPoint3DProps = {
  position?: [number, number, number];
  color: string;
  radius?: number;
  opacity?: number;
  selected?: boolean;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
  onContextMenu?: (event: ThreeEvent<PointerEvent>) => void;
  onHoverChange?: (hovered: boolean) => void;
  dragHandlers?: DirectPointDragHandlers;
};

const DEFAULT_POINT_RADIUS = 0.09;
const POINT_HIT_RADIUS = 0.2;

const DuctPoint3D = forwardRef<Mesh, DuctPoint3DProps>(
  (
    {
      position,
      color,
      radius = DEFAULT_POINT_RADIUS,
      opacity = 1,
      selected = false,
      onClick,
      onContextMenu,
      onHoverChange,
      dragHandlers
    },
    ref
  ) => {
    const [hovered, setHovered] = useState(false);
    const rightClick = use3DRightClick(onContextMenu);
    const hoveredRef = useRef(false);
    const onHoverChangeRef = useRef(onHoverChange);

    onHoverChangeRef.current = onHoverChange;

    useEffect(() => {
      return () => {
        if (hoveredRef.current) {
          hoveredRef.current = false;
          document.body.style.cursor = '';
          onHoverChangeRef.current?.(false);
        }
      };
    }, []);

    return (
      <mesh
        ref={ref}
        position={position}
        scale={hovered && !selected ? 1.35 : 1}
        renderOrder={getOpacityRenderOrder(opacity)}
        userData={{ isDuctRunPoint: true }}
        onClick={onClick}
        onPointerDown={(event) => {
          rightClick.onPointerDown(event);
          dragHandlers?.onPointerDown(event);
        }}
        onPointerMove={dragHandlers?.onPointerMove}
        onPointerUp={(event) => {
          rightClick.onPointerUp(event);
          dragHandlers?.onPointerUp(event);
        }}
        onPointerCancel={dragHandlers?.onPointerCancel}
        onPointerOver={(event) => {
          event.stopPropagation();
          setHovered(true);
          hoveredRef.current = true;
          document.body.style.cursor = 'grab';
          onHoverChange?.(true);
        }}
        onPointerOut={() => {
          setHovered(false);
          hoveredRef.current = false;
          document.body.style.cursor = '';
          onHoverChange?.(false);
        }}
      >
        <sphereGeometry args={[radius, 16, 16]} />
        <meshStandardMaterial
          color={color}
          emissive={selected ? '#ffffff' : '#000000'}
          emissiveIntensity={selected ? 0.4 : 0}
          {...getOpacityMaterialProps(opacity)}
        />
        <mesh userData={{ isDuctRunPoint: true }}>
          <sphereGeometry args={[POINT_HIT_RADIUS, 8, 8]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      </mesh>
    );
  }
);

DuctPoint3D.displayName = 'DuctPoint3D';

export default DuctPoint3D;
